import { cn } from '~/utils/cn';
import { formatDateShort, relativeDueLabel } from '~/utils/contractLabels';
import { formatCentsToBRL } from '~/utils/formatFinance';
import type { ContractInstallment } from '~/store/contracts/interfaces';

import { Icon } from '../../atoms/Icon/view';
import { Text } from '../../atoms/Text/view';

interface PropsSelectableInstallmentRow {
  installment: ContractInstallment;
  selected: boolean;
  disabled?: boolean;
  contractLabel?: string;
  onToggle: () => void;
  className?: string;
}

const STATUS_LABEL: Record<ContractInstallment['status'], string> = {
  paid: 'Paga',
  next: 'A vencer',
  future: 'Futura',
  overdue: 'Vencida',
};

const STATUS_BG: Record<ContractInstallment['status'], string> = {
  paid: 'bg-success-01',
  next: 'bg-alert-01',
  future: 'bg-intense-02',
  overdue: 'bg-warning-01',
};

const STATUS_TEXT: Record<ContractInstallment['status'], string> = {
  paid: 'color-success-05',
  next: 'color-alert-06',
  future: 'color-intense-06',
  overdue: 'color-warning-05',
};

export default function SelectableInstallmentRow({
  installment,
  selected,
  disabled,
  contractLabel,
  onToggle,
  className,
}: PropsSelectableInstallmentRow) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        'flex w-full flex-row items-center gap-smaller rounded-[14px] bg-intense-01 border p-smaller text-left',
        selected ? 'border-primary-01' : 'border-intense-03',
        disabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-intense-02',
        className,
      )}
    >
      <span
        className={cn(
          'flex w-[22px] h-[22px] shrink-0 items-center justify-center rounded-[6px] border-2',
          selected
            ? 'bg-primary-01 border-primary-01'
            : 'bg-intense-01 border-intense-04',
        )}
      >
        {selected ? (
          <Icon name="icon-check" size={12} color="#FFFFFF" rounded={false} />
        ) : null}
      </span>

      <span className="flex-1 min-w-0">
        <Text className="text-bodyLG color-complement-01">
          {`Parcela ${installment.index}`}
        </Text>
        {contractLabel ? (
          <Text className="mt-quark text-captionMD color-intense-05 truncate">
            {contractLabel}
          </Text>
        ) : null}
        <Text className="mt-quark text-captionMD color-intense-06">
          {`${formatDateShort(installment.dueDate)} · ${relativeDueLabel(
            installment.dueDate,
          )}`}
        </Text>
      </span>

      <span className="flex flex-col items-end shrink-0">
        <Text className="text-bodyLG color-complement-01">
          {formatCentsToBRL(installment.amount)}
        </Text>
        <span
          className={cn(
            'mt-quark rounded-full px-nano py-quark',
            STATUS_BG[installment.status],
          )}
        >
          <Text
            className={cn('text-caption', STATUS_TEXT[installment.status])}
          >
            {STATUS_LABEL[installment.status]}
          </Text>
        </span>
      </span>
    </button>
  );
}
